/** @typedef {import('@sentry/core').ErrorEvent} ErrorEvent */
/** @typedef {import('@sentry/core').EventHint} EventHint */
/** @typedef {import('@sentry/core').Event} SentryEvent */
/** @typedef {import('@sentry/core').TransactionEvent} TransactionEvent */
/** @typedef {import('@sentry/core').Breadcrumb} Breadcrumb */

/**
 * Expected operational failures we do not want as Sentry issues — wrong
 * passwords, invite-gated registration, client disconnects, stale webpack
 * chunks after deploy, and tracing-only performance hints.
 *
 * Keep real defects (hydration mismatches, invalid filter objects, 5xx) out
 * of this list. When adding a pattern, note why it is expected user/ops
 * noise rather than a product bug.
 */
const IGNORE_ERRORS = [
  "Invalid user credentials.",
  /hasn't been invited/i,
  "Token expired.",
  "jwt expired",
  "Session expired - please login again",
  "Not authorized",
  "Network error - please check your connection",
  "Request timeout - please try again",
  /^Too many requests\b/,
  /^Error: aborted$/,
  "aborted",
  /loading chunk \d+ failed/i,
  /chunkloaderror/i,
  /failed to fetch dynamically imported module/i,
  /importing a module script failed/i,
  /dynamically imported module/i,
];

const AUTH_ROUTE = /\/auth\/(?:login|register|forgot-password|sso-callback)/;

const AUTH_EXPECTED_MESSAGES = [
  "Invalid user credentials.",
  "Token expired.",
  "Session expired - please login again",
  "Not authorized",
  "Bad request",
];

const PERF_INFO_PATTERNS = /Slow DB Query|N\+1 (?:Query|API Call)/i;

const STALE_CHUNK_PATTERNS = [
  /loading chunk \d+ failed/i,
  /chunkloaderror/i,
  /failed to fetch dynamically imported module/i,
  /importing a module script failed/i,
  /dynamically imported module/i,
];

/**
 * @param {unknown} error
 * @returns {string | undefined}
 */
function errorMessage(error) {
  if (error == null) return undefined;
  if (typeof error === "string") return error.length > 0 ? error : undefined;
  if (error instanceof Error)
    return error.message.length > 0 ? error.message : undefined;
  if (typeof error !== "object") return undefined;

  const message = /** @type {{ message?: unknown }} */ (error).message;
  if (typeof message === "string" && message.length > 0) {
    return message;
  }

  return undefined;
}

/**
 * @param {ErrorEvent} event
 * @param {EventHint} [hint]
 * @returns {string | undefined}
 */
function eventMessage(event, hint) {
  const fromException = event.exception?.values?.[0]?.value;
  if (typeof fromException === "string" && fromException.length > 0) {
    return fromException;
  }
  if (typeof event.message === "string" && event.message.length > 0) {
    return event.message;
  }
  return errorMessage(hint?.originalException);
}

/**
 * @param {string | undefined} message
 * @param {Array<string | RegExp>} patterns
 * @returns {boolean}
 */
function matchesAnyPattern(message, patterns) {
  if (!message) return false;
  return patterns.some((pattern) =>
    typeof pattern === "string" ? message === pattern : pattern.test(message),
  );
}

/**
 * @param {ErrorEvent | undefined} event
 * @returns {string}
 */
function routeOf(event) {
  if (!event) return "";
  return (
    event.transaction ||
    event.request?.url ||
    event.tags?.route ||
    event.tags?.url ||
    ""
  );
}

/**
 * @param {string | undefined} message
 * @param {ErrorEvent} [event]
 * @returns {boolean}
 */
function isExpectedOperationalError(message, event) {
  if (matchesAnyPattern(message, IGNORE_ERRORS)) return true;

  const route = routeOf(event);
  if (
    AUTH_ROUTE.test(route) &&
    matchesAnyPattern(message, AUTH_EXPECTED_MESSAGES)
  ) {
    return true;
  }

  return false;
}

/**
 * @param {string | undefined} message
 * @returns {boolean}
 */
function isStaleChunkLoadError(message) {
  return matchesAnyPattern(message, STALE_CHUNK_PATTERNS);
}

/**
 * @param {ErrorEvent} event
 * @param {EventHint} hint
 * @param {"expected operational error" | "performance trace"} reason
 */
function logFilteredFromReporting(event, hint, reason) {
  const message = eventMessage(event, hint);
  const route = routeOf(event);
  const original = hint?.originalException;
  const suffix = route ? ` @ ${route}` : "";
  const prefix = `[observability] dropped from error reporting (${reason})${suffix}:`;

  // Filtered events are intentionally kept out of Sentry — log locally so dev
  // tools / container stdout still show them. console.error is NOT wired to
  // Sentry in our SDK config (no captureConsole integration). console.warn (not
  // debug) so the dropped-event record survives collectors that discard debug.
  const log = console.warn;

  if (original instanceof Error) {
    log(prefix, original);
    return;
  }

  log(prefix, message ?? original ?? event);
}

// Credentials travel in query strings (`?access_token=`, reset and invite
// links), in the Authorization header and in cookies. Sentry records all three
// on requests, breadcrumbs and spans, so every URL loses its query and every
// credential header and cookie is dropped before anything is sent.
const CREDENTIAL_HEADERS = new Set([
  "authorization",
  "proxy-authorization",
  "cookie",
  "set-cookie",
  "x-api-key",
]);

// Span and breadcrumb attributes that hold a whole URL or request target.
const URL_ATTRIBUTES = new Set([
  "url",
  "from",
  "to",
  "url.full",
  "http.url",
  "http.target",
]);

// Headers whose value is a URL.
const URL_HEADERS = new Set(["referer", "referrer", "location"]);

// Span and breadcrumb attributes that hold only a query or fragment.
const QUERY_ATTRIBUTES = new Set([
  "url.query",
  "http.query",
  "url.fragment",
  "http.fragment",
]);

/**
 * @param {string} url
 * @returns {string}
 */
function withoutQuery(url) {
  const end = url.search(/[?#]/);
  return end === -1 ? url : url.slice(0, end);
}

// URLs inside free text, matched the way apps/web's sentry-transport.ts does:
// an absolute URL, a rooted path, or a bare `?key=value` query.
const ABSOLUTE_URL_WITH_QUERY =
  /([a-z][a-z\d+.-]*:\/\/[^\s?#"<>]*)\?[^\s#"<>]*/gi;
const ROOTED_PATH_WITH_QUERY = /(^|[^\w.:/])(\/[^\s?#"<>]*)\?[^\s#"<>]*/g;
const KEY_VALUE_QUERY = /\?[^\s#"<>?=]*=[^\s#"<>]*/g;

/**
 * @param {string} text
 * @returns {string}
 */
function withoutQueriesInText(text) {
  return text
    .replace(ABSOLUTE_URL_WITH_QUERY, "$1")
    .replace(ROOTED_PATH_WITH_QUERY, "$1$2")
    .replace(KEY_VALUE_QUERY, "");
}

/**
 * A URL, or the list of URLs OpenTelemetry records for a header, without
 * queries.
 *
 * @param {unknown} value
 * @returns {unknown}
 */
function withoutQueries(value) {
  if (typeof value === "string") return withoutQuery(value);
  if (Array.isArray(value)) return value.map(withoutQueries);
  return value;
}

/**
 * @param {Record<string, unknown> | undefined} attributes
 */
function scrubAttributes(attributes) {
  if (!attributes) return;
  for (const [key, value] of Object.entries(attributes)) {
    const lower = key.toLowerCase();
    const header = lower
      .match(/^http\.(?:request|response)\.header\.(.+)$/)?.[1]
      .split(".")[0];
    if (
      QUERY_ATTRIBUTES.has(lower) ||
      (header && CREDENTIAL_HEADERS.has(header))
    ) {
      delete attributes[key];
    } else if (
      URL_ATTRIBUTES.has(lower) ||
      (header && URL_HEADERS.has(header))
    ) {
      attributes[key] = withoutQueries(value);
    }
  }
}

/**
 * @param {Breadcrumb} breadcrumb
 * @returns {Breadcrumb}
 */
function scrubBreadcrumb(breadcrumb) {
  if (typeof breadcrumb.message === "string") {
    breadcrumb.message = withoutQueriesInText(breadcrumb.message);
  }
  // Console breadcrumbs repeat their message, unscrubbed, as `arguments`.
  if (breadcrumb.category === "console") delete breadcrumb.data?.arguments;
  scrubAttributes(breadcrumb.data);
  return breadcrumb;
}

/**
 * Strip query strings, credential headers and cookies from an event or
 * transaction, in place.
 *
 * @template {SentryEvent} T
 * @param {T} event
 * @returns {T}
 */
function scrubEvent(event) {
  const request = event.request;
  if (request) {
    if (typeof request.url === "string")
      request.url = withoutQuery(request.url);
    delete request.query_string;
    delete request.cookies;
    const headers = request.headers ?? {};
    for (const [name, value] of Object.entries(headers)) {
      const lower = name.toLowerCase();
      if (CREDENTIAL_HEADERS.has(lower)) delete headers[name];
      else if (URL_HEADERS.has(lower)) headers[name] = withoutQuery(value);
    }
  }
  if (typeof event.transaction === "string") {
    event.transaction = withoutQueriesInText(event.transaction);
  }
  if (typeof event.message === "string") {
    event.message = withoutQueriesInText(event.message);
  }
  if (typeof event.logentry?.message === "string") {
    event.logentry.message = withoutQueriesInText(event.logentry.message);
  }
  for (const exception of event.exception?.values ?? []) {
    if (typeof exception.value === "string") {
      exception.value = withoutQueriesInText(exception.value);
    }
  }
  for (const image of event.debug_meta?.images ?? []) {
    if ("code_file" in image && typeof image.code_file === "string") {
      image.code_file = withoutQuery(image.code_file);
    }
  }
  for (const breadcrumb of event.breadcrumbs ?? []) scrubBreadcrumb(breadcrumb);
  scrubAttributes(event.contexts?.trace?.data);
  for (const span of event.spans ?? []) {
    scrubAttributes(span.data);
    if (typeof span.description === "string") {
      span.description = withoutQueriesInText(span.description);
    }
  }
  return event;
}

/**
 * @param {ErrorEvent} event
 * @param {EventHint} hint
 * @returns {ErrorEvent | null}
 */
function beforeSend(event, hint) {
  const message = eventMessage(event, hint);

  if (isExpectedOperationalError(message, event)) {
    logFilteredFromReporting(event, hint, "expected operational error");
    return null;
  }

  if (event.level === "info" && PERF_INFO_PATTERNS.test(message ?? "")) {
    logFilteredFromReporting(event, hint, "performance trace");
    return null;
  }

  return scrubEvent(event);
}

/**
 * @param {TransactionEvent} event
 * @returns {TransactionEvent}
 */
function beforeSendTransaction(event) {
  return scrubEvent(event);
}

/**
 * @param {Breadcrumb} breadcrumb
 * @returns {Breadcrumb}
 */
function beforeBreadcrumb(breadcrumb) {
  return scrubBreadcrumb(breadcrumb);
}

/**
 * Only send Express-handled errors to Sentry when they look like server faults.
 * YP 4xx responses are normal control flow and should stay in access logs only.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
function shouldCaptureExpressError(error) {
  if (!error || typeof error !== "object") return true;

  const status =
    /** @type {{ status?: unknown; statusCode?: unknown }} */ (error).status ??
    /** @type {{ statusCode?: unknown }} */ (error).statusCode;

  if (typeof status === "number") {
    return status >= 500;
  }

  return true;
}

module.exports = {
  IGNORE_ERRORS,
  beforeBreadcrumb,
  beforeSend,
  beforeSendTransaction,
  isExpectedOperationalError,
  isStaleChunkLoadError,
  shouldCaptureExpressError,
};
